import * as React from 'react';
import { isSuperAdmin } from '../components_v2/utils';
import { LoadingStateEnum } from '../containers_v2/import/model';
import PermissionContext from '../containers_v2/permissions/PermissionContext';
import { isUserAllowedToUpdateForms } from '../containers_v2/settings/userSettings/actions';
import { Deserializer, getLocalStorage, Serializer, setLocalStorage } from '../helpers/localStorage';

interface StateProps<S> {
	initialState: S | (() => S),
	fn?: (s: { oldValue: S, newValue: S }) => S
}

export function useLatest<T>(value: T): React.MutableRefObject<T> {
	const ref = React.useRef(value);
	React.useLayoutEffect(() => {
		ref.current = value;
	});
	return ref;
}

/**
 * Same as React.useState but apply a function whenever the state is updated
 * The function is not called when the state is initialized
 * @param  {S} initialState The initial state
 * @param  {(s: { oldValue: S, newValue: S }) => S} fn Function called every time the state is updated
 * @param  {(newValue: S) => void} onNewValueSet Optional function to send the new value
 * This function has 2 parameters, the old value, and the new one.
 * The function return the new state
 * @return {[S, React.Dispatch<React.SetStateAction<S>>, React.Dispatch<React.SetStateAction<S>>]} The state, the setter, and the setter without the function
 */
export function useFunctionState<S = undefined>(initialState: StateProps<S>['initialState'], fn?: StateProps<S>['fn'], onNewValueSet?: (newValue: S) => void): [S, React.Dispatch<React.SetStateAction<S>>, React.Dispatch<React.SetStateAction<S>>] {
	const [state, setState_] = React.useState(initialState);
	const latest = React.useRef(state);
	const callbackRef = useLatest(fn);
	const onNewValueSetRef = useLatest(onNewValueSet);
	const resolve = (input: React.SetStateAction<S>): S => typeof input === 'function' ? (input as (oldValue: S) => S)(latest.current) : input;
	const setState = React.useCallback((input: React.SetStateAction<S>) => {
		const oldValue = latest.current;
		const newValue = resolve(input);
		const res = callbackRef.current ? callbackRef.current({ oldValue, newValue }) : newValue;
		onNewValueSetRef.current?.(res);
		latest.current = res;
		setState_(res);
	}, []);
	const setStateNoFn = React.useCallback((input: React.SetStateAction<S>) => {
		const newValue = resolve(input);
		latest.current = newValue;
		setState_(newValue);
	}, []);
	return [state, setState, setStateNoFn];
}

/**
 * Same as useFunctionState but doesn't return the value of the state but a mutable reference on it
 * @see useFunctionState for implementation
 * @param  {S} initialState The initial state
 * @param  {(s: { oldValue: S, newValue: S }) => S} fn Function called every time the state is updated
 * This function has 2 parameters, the old value, and the new one.
 * The function return the new state
 * @return {[S, React.Dispatch<React.SetStateAction<S>>]} The same as React.useState
 */
export function useRefState<S = undefined>(initialState: StateProps<S>['initialState'], fn?: StateProps<S>['fn']): [React.MutableRefObject<S>, React.Dispatch<React.SetStateAction<S>>, S] {
	const [state_, setState] = useFunctionState(initialState, (p: { oldValue: S, newValue: S }) => {
		let value = p.newValue;
		if (fn) value = fn(p);
		state.current = value;
		return value;
	});
	const state = React.useRef(state_);
	return [state, setState, state_];
}



/**
 * Same as React.useState but init from local storage and save to local storage on update
 * @param  {string} key The key used to store the state in local storage
 * @param  {S} initialState The initial state
 * @param  {Serializer<S, Ser>} serializer Function called every time the state is updated
 * @param  {Deserializer<S, Ser>} deserializer Function called every time the state is read from local storage
 * @return {[S, React.Dispatch<React.SetStateAction<S>>, React.Dispatch<React.SetStateAction<S>>]} The state, the setter with localStorage, and the setter without settings in localStorage
 */
export function useLocalStorageState<S = undefined, Ser = undefined>(key: string, initialState: StateProps<S>['initialState'], serializer?: Serializer<S, Ser>, deserializer?: Deserializer<S, Ser | undefined>): [S, React.Dispatch<React.SetStateAction<S>>, React.Dispatch<React.SetStateAction<S>>] {
	return useFunctionState(getLocalStorage(key, deserializer) ?? initialState, undefined, (state) => setLocalStorage(key, state, serializer));
}

/**
 * Load the script given in params and return the state
 * @param  {string} url The script's url
 * @return {LoadingStateEnum} The current state of the script
 */
export const useExternalScript = (url: string): LoadingStateEnum => {
	const [state, setState] = React.useState<LoadingStateEnum>(url ? LoadingStateEnum.LOADING : LoadingStateEnum.ERROR);

	React.useEffect(() => {
		if (!url) {
			setState(LoadingStateEnum.ERROR);
			return;
		}

		let script = document.querySelector(`script[src="${url}"]`);

		const handleScript = (e) => {
			setState(e.type === 'load' ? LoadingStateEnum.LOADED : LoadingStateEnum.ERROR);
		};

		if (!script) {
			script = document.createElement('script');
			// @ts-expect-error it works
			script.type = 'application/javascript';
			// @ts-expect-error it works
			script.src = url;
			// @ts-expect-error it works
			script.async = true;
			document.body.appendChild(script);
		}
		script.addEventListener('load', handleScript);
		script.addEventListener('error', handleScript);

		return () => {
			script?.removeEventListener('load', handleScript);
			script?.removeEventListener('error', handleScript);
		};
	}, [url]);

	return state;
};

type Key = {
	key: string,
	ctrlKey?: boolean,
	altKey?: boolean,
	shiftKey?: boolean,
	metaKey?: boolean
};

/**
 * Call a function when a key is pressed
 * @param  {Key[]} keys keys to listen to
 * @param  {(key: Key, ev: KeyboardEvent) => void} fn Function called every time a listened key is pressed
 * @param  {Node} node The node where the event listener is attached
 * @param  {boolean} noPreventDefault If true, the default behavior of the key is not prevented
 */
export function useKeyPress(keys: Key[], callback?: (key: Key, ev: KeyboardEvent) => void, node?: Node, noPreventDefault?: boolean) {
	// implement the callback ref pattern
	const callbackRef = React.useRef(callback);
	React.useLayoutEffect(() => {
		callbackRef.current = callback;
	});

	const handleKeyPress = React.useCallback(
		(event: KeyboardEvent) => {
			const key = keys.find((key) => {
				if (event.key !== key.key) return false;
				if (key.altKey && !event.altKey) return false;
				if (key.ctrlKey && !event.ctrlKey) return false;
				if (key.shiftKey && !event.shiftKey) return false;
				if (key.metaKey && !event.metaKey) return false;
				return true;
			});
			if (key !== undefined) {
				if (!noPreventDefault) event.preventDefault();
				callbackRef.current?.(key, event);
			}
		},
		[keys]
	);

	React.useEffect(() => {
		// target is either the provided node or the document
		const targetNode = node ?? document;
		if (targetNode) targetNode.addEventListener('keydown', handleKeyPress);

		return () => targetNode && targetNode.removeEventListener('keydown', handleKeyPress);
	}, [handleKeyPress, node]);
}

/**
 * hook that returns whether the user is allowed to edit forms
 * @return {boolean} are you allowed to edit forms
 */
export function useEditFormsPermission(): boolean {
	const is_super_admin = isSuperAdmin();
	const { isAllowedTo } = React.useContext(PermissionContext);
	const [isAllowedToEditForms, setIsAllowedToEditForms] = React.useState<boolean>(is_super_admin || isAllowedTo({ objectAction: 'CreateFormSettings' }));

	React.useEffect(() => {
		if (!isAllowedToEditForms) {
			isUserAllowedToUpdateForms().then(setIsAllowedToEditForms);
		}
	}, []);
	return isAllowedToEditForms;
}

/**
 * hook that returns style to apply to an element to be clamped to the viewport
 * @param  {boolean} open is the popup open?
 * @return {[React.RefObject<HTMLDivElement>, React.CSSProperties]} ref to set on the element to be clamped, and the style to apply
 */
export function useClampToViewport(open: boolean): [React.RefObject<HTMLDivElement>, React.CSSProperties] {
	const ref = React.useRef<HTMLDivElement>(null);
	const [shift, setShift] = React.useState(0);

	React.useLayoutEffect(() => {
		if (!open || !ref.current) return;
		const m = 8;
		const r = ref.current.getBoundingClientRect();
		const overRight = r.right - (window.innerWidth - m);
		const overLeft = m - r.left;
		if (overRight > 0) setShift(s => s - overRight);
		else if (overLeft > 0) setShift(s => s + overLeft);
	}, [open]);

	return [ref, { transform: `translateX(${shift}px)` }];
}
